import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CallModal } from "@/components/Modals/CallModal";
import { CallMinimized } from "@/components/Calls/CallMinimized";
import useToast from "@/hooks/useToast";
import { useStorage } from "@/hooks/useStorage";
import { useAudioPlayer } from "expo-audio";
import useAuth from "@/hooks/useAuth";
import * as TwilioVoice from "@/lib/twilio";
import { CallContext } from "@/contexts/CallContext";
import type { CallState, CallData, MinimizedPosition, CallProviderProps } from "@/types/contexts/call";
import { supabase } from "@/lib/supabase-client";
import { formatPhoneNumber } from "@/lib/phone";
import { callService } from "@/services/callService";
import { userService } from "@/services/userService";

const STORAGE_KEY = "@call_minimized_position";

// Audio assets
const RINGTONE_AUDIO = require("@/assets/audio/phone-ringing.mp3");
const CALL_CONNECT_AUDIO = require("@/assets/audio/ui-sound-on.mp3");
const CALL_END_AUDIO = require("@/assets/audio/ui-sound-off.mp3");

export function CallProvider({ children }: PropsWithChildren) {
  const [callState, setCallState] = useState<CallState>("idle");
  const [currentCall, setCurrentCall] = useState<CallData | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [minimizedPosition, setMinimizedPosition] = useState<MinimizedPosition | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeaker, setIsSpeaker] = useState(false);
  const counterRef = useRef(0);
  const toast = useToast();
  const storage = useStorage();
  const { user, session } = useAuth();
  const voiceInitialized = useRef(false);

  // Audio players
  const ringtonePlayer = useAudioPlayer(RINGTONE_AUDIO);
  const connectSoundPlayer = useAudioPlayer(CALL_CONNECT_AUDIO);
  const endSoundPlayer = useAudioPlayer(CALL_END_AUDIO);

  // Load saved position on mount
  useEffect(() => {
    const savedPositionStr = storage.getString(STORAGE_KEY);
    if (savedPositionStr) {
      try {
        const savedPosition = JSON.parse(savedPositionStr) as MinimizedPosition;
        setMinimizedPosition(savedPosition);
      } catch (error) {
        console.warn("Failed to parse saved call position:", error);
      }
    }
  }, [storage]);

  // Register Voice SDK as soon as the user is authenticated so we can
  // receive incoming calls without having to make an outbound call first.
  useEffect(() => {
    if (!user || !session || voiceInitialized.current) return;

    let cancelled = false;

    (async () => {
      try {
        const token = await TwilioVoice.fetchVoiceToken(user.id, session.access_token);
        if (cancelled) return;
        await TwilioVoice.initializeTwilioVoice(token);
        voiceInitialized.current = true;
        console.log("[CallProvider] Voice SDK registered for incoming calls");
      } catch (err) {
        console.warn("[CallProvider] Failed to initialize Voice SDK on mount:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, session]);

  // Provide a token refresher so the Twilio device can recover from
  // WebSocket drops and proactively refresh expiring tokens.
  useEffect(() => {
    if (!user || !session) return;
    TwilioVoice.setTokenRefresher(() => TwilioVoice.fetchVoiceToken(user.id, session.access_token));
    return () => TwilioVoice.setTokenRefresher(null);
  }, [user, session]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (voiceInitialized.current) {
        TwilioVoice.cleanup();
        voiceInitialized.current = false;
      }
    };
  }, []);

  // Save position to storage when it changes
  useEffect(() => {
    if (minimizedPosition) {
      storage.saveFast(STORAGE_KEY, JSON.stringify(minimizedPosition));
    }
  }, [minimizedPosition, storage]);

  // Helper: Stop ringtone
  const stopRingtone = useCallback(() => {
    ringtonePlayer.pause();
    ringtonePlayer.seekTo(0);
  }, [ringtonePlayer]);

  // Helper: Play ringtone
  const playRingtone = useCallback(() => {
    ringtonePlayer.seekTo(0);
    ringtonePlayer.loop = true;
    ringtonePlayer.play();
  }, [ringtonePlayer]);

  // Helper: Play connect sound
  const playConnectSound = useCallback(() => {
    connectSoundPlayer.seekTo(0);
    connectSoundPlayer.play();
  }, [connectSoundPlayer]);

  // Helper: Play end sound
  const playEndSound = useCallback(() => {
    endSoundPlayer.seekTo(0);
    endSoundPlayer.play();
  }, [endSoundPlayer]);

  // Look up a user by their cell phone number (normalized 10-digit match)
  const lookupUserByPhone = useCallback(async (phoneNumber: string) => {
    try {
      const digits = phoneNumber.replace(/\D/g, "");
      const normalized = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
      if (!normalized) return null;

      const { data } = await supabase.from("users").select("id, first_name, last_name, preferred_name, meta_data");

      if (!data) return null;

      const found = data.find((u) => {
        const phones = (u.meta_data as any)?.phones || [];
        return phones.some((p: any) => {
          if (p.type !== "cell" || !p.number) return false;
          const d = p.number.replace(/\D/g, "");
          const n = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
          return n === normalized;
        });
      });

      if (!found) return null;
      const name = found.preferred_name || `${found.first_name || ""} ${found.last_name || ""}`.trim() || null;
      return { id: found.id, name };
    } catch {
      return null;
    }
  }, []);

  // Handle incoming call
  const handleIncomingCall = useCallback(
    (event: TwilioVoice.TwilioCallEvent) => {
      const id = `call_${Date.now()}_${counterRef.current++}`;

      const fromNumber = event.from || "Unknown";
      const toNumber = event.to || "";

      console.log("[CallProvider] Incoming call from", fromNumber, "to", toNumber);

      // Start ringtone for incoming call
      playRingtone();

      setCallState("incoming");
      setIsMinimized(false);

      // Show modal immediately with the phone number
      setCurrentCall({
        id,
        callerName: formatPhoneNumber(fromNumber),
        phoneNumber: fromNumber,
        fromNumber: toNumber, // The "to" is our number
        direction: "inbound",
        callSid: event.callSid,
      });

      // Async: try to resolve the caller's identity for avatar + name
      lookupUserByPhone(fromNumber).then((match) => {
        if (match) {
          setCurrentCall((prev) =>
            prev && prev.id === id
              ? { ...prev, callerName: match.name || prev.callerName, callerUserId: match.id }
              : prev,
          );
        }
      });
    },
    [playRingtone, lookupUserByPhone],
  );

  // When another user answers an incoming call meant for us
  const handleAnsweredElsewhere = useCallback(
    async (callSid?: string) => {
      stopRingtone();
      setCallState("idle");
      setCurrentCall(null);
      setIsMinimized(false);

      if (!callSid || !user) {
        toast?.showToast({
          title: "Call Answered",
          message: "Another user picked up the call",
          toastType: "info",
        });
        return;
      }

      // Small delay so the status callback can update the call_history record
      await new Promise((r) => setTimeout(r, 1500));

      try {
        const record = await callService.getCallBySid(callSid);
        if (record?.user_id && record.user_id !== user.id) {
          const answerer = await userService.getUserById(record.user_id);
          if (answerer) {
            const name =
              answerer.preferred_name ||
              `${answerer.first_name || ""} ${answerer.last_name || ""}`.trim() ||
              "Another user";
            toast?.showToast({
              title: "Call Answered",
              message: `${name} picked up the call`,
              toastType: "info",
            });
            return;
          }
        }
      } catch {
        // Fall through to generic toast
      }

      toast?.showToast({
        title: "Call Answered",
        message: "Another user picked up the call",
        toastType: "info",
      });
    },
    [stopRingtone, user, toast],
  );

  // Set up Twilio Voice event listener
  useEffect(() => {
    const listener: TwilioVoice.TwilioCallEventListener = (event) => {
      console.log("[CallProvider] Twilio event:", event.type);

      switch (event.type) {
        case "incoming":
          handleIncomingCall(event);
          break;
        case "answered_elsewhere":
          handleAnsweredElsewhere(event.callSid);
          break;
        case "ringing":
          setCallState("outgoing");
          break;
        case "connected":
          stopRingtone();
          setCallState("active");
          playConnectSound();
          break;
        case "disconnected":
        case "failed":
          stopRingtone();
          endCall();
          break;
      }
    };

    TwilioVoice.addEventListener(listener);

    return () => {
      TwilioVoice.removeEventListener(listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopRingtone, playConnectSound]);

  const startCall = useCallback<CallProviderProps["startCall"]>(
    async (data) => {
      if (!user || !session) {
        toast?.showToast({
          title: "Not Authenticated",
          message: "Please log in to make calls",
          toastType: "error",
        });
        return;
      }

      const id = `call_${Date.now()}_${counterRef.current++}`;

      // Start ringtone
      playRingtone();

      setCallState("outgoing");
      setIsMinimized(false);

      // Store call data
      setCurrentCall({
        id,
        ...data,
        startTime: new Date(),
        direction: "outbound",
      });

      try {
        // Initialize Voice SDK on first call
        if (!voiceInitialized.current) {
          const token = await TwilioVoice.fetchVoiceToken(user.id, session.access_token);
          await TwilioVoice.initializeTwilioVoice(token);
          voiceInitialized.current = true;
        }

        // Make call using Twilio Voice SDK
        const customParams: Record<string, string> = {};
        if (data.fromNumber) customParams.From = data.fromNumber;
        if (user.id) customParams.UserId = user.id;
        await TwilioVoice.makeCall(
          data.phoneNumber || "",
          Object.keys(customParams).length > 0 ? customParams : undefined,
        );
      } catch (err) {
        toast?.showToast({
          title: "Call Failed",
          message: err instanceof Error ? err.message : "Failed to initiate call",
          toastType: "error",
        });
        stopRingtone();
        setCallState("idle");
        setCurrentCall(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [playRingtone, stopRingtone, user, session],
  );

  const endCall = useCallback(() => {
    TwilioVoice.endCall();

    stopRingtone();

    if (currentCall?.startTime) {
      const durationMs = Date.now() - currentCall.startTime.getTime();
      const durationSec = Math.floor(durationMs / 1000);
      const minutes = Math.floor(durationSec / 60);
      const seconds = durationSec % 60;

      const durationText = minutes > 0 ? `${minutes} min ${seconds} sec` : `${seconds} sec`;

      playEndSound();

      toast?.showToast({
        title: `Call with ${currentCall.callerName} ended`,
        message: `Duration: ${durationText}`,
        toastType: "info",
      });
    }

    setCallState("idle");
    setCurrentCall(null);
    setIsMinimized(false);
    setIsMuted(false);
    setIsSpeaker(false);
  }, [currentCall, toast, stopRingtone, playEndSound]);

  const answerCall = useCallback(async () => {
    if (callState === "incoming") {
      try {
        await TwilioVoice.acceptCall();
        stopRingtone();
        setCurrentCall((prev) => (prev ? { ...prev, startTime: new Date() } : null));
        // State will be updated to "active" by the "connected" event

        // Stamp our user_id on the call_history record so other users
        // can see who answered via the answered_elsewhere flow.
        if (currentCall?.callSid && user?.id) {
          supabase
            .from("call_history")
            .update({ user_id: user.id })
            .eq("call_sid", currentCall.callSid)
            .then(({ error: err }) => {
              if (err) console.warn("[CallProvider] Failed to stamp user_id on call:", err.message);
            });
        }
      } catch (error) {
        console.error("[CallProvider] Failed to answer call:", error);
        toast?.showToast({
          title: "Call Failed",
          message: "Failed to answer the call",
          toastType: "error",
        });
        stopRingtone();
        setCallState("idle");
        setCurrentCall(null);
      }
    }
  }, [callState, stopRingtone, toast, currentCall?.callSid, user?.id]);

  const rejectCall = useCallback(async () => {
    if (callState === "incoming") {
      try {
        await TwilioVoice.rejectCall();
        stopRingtone();
        playEndSound();
        setCallState("idle");
        setCurrentCall(null);
        setIsMinimized(false);
      } catch (error) {
        console.error("[CallProvider] Failed to reject call:", error);
        // Still clean up the UI
        stopRingtone();
        setCallState("idle");
        setCurrentCall(null);
      }
    }
  }, [callState, stopRingtone, playEndSound]);

  const toggleMinimize = useCallback(() => {
    setIsMinimized((prev) => !prev);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const newMuted = !prev;
      TwilioVoice.setMuted(newMuted);

      // Play sound when toggling mute
      if (newMuted) {
        playEndSound();
      } else {
        playConnectSound();
      }

      return newMuted;
    });
  }, [playConnectSound, playEndSound]);
  const toggleSpeaker = useCallback(() => setIsSpeaker((prev) => !prev), []);

  const ctx = useMemo(
    () => ({
      callState,
      currentCall,
      startCall,
      endCall,
      answerCall,
      rejectCall,
      toggleMinimize,
      isMinimized,
      minimizedPosition,
      setMinimizedPosition,
      isMuted,
      toggleMute,
      isSpeaker,
      toggleSpeaker,
    }),
    [
      callState,
      currentCall,
      startCall,
      endCall,
      answerCall,
      rejectCall,
      toggleMinimize,
      isMinimized,
      minimizedPosition,
      isMuted,
      toggleMute,
      isSpeaker,
      toggleSpeaker,
    ],
  );

  const showFullModal = !isMinimized && callState !== "idle";
  const showMinimized = isMinimized && callState !== "idle";

  return (
    <CallContext.Provider value={ctx}>
      {children}
      {showFullModal && <CallModal />}
      {showMinimized && <CallMinimized />}
    </CallContext.Provider>
  );
}
